import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AbstractWidgetComponent, ContextChange, MasterDetailListEntry, ResponseMessage, ResponseStatus, TranslationService, WidgetStyle } from '@sdbn/common';
import {
  TransportCreationProfile, TransportCreationProfileNumberPlate,
} from '../transport-creation-profiles.model';
import { TmcContextService } from './../../../../common/tmc-context.service';

import { FormControl, Validators } from '@angular/forms';
import { AppServerAPI, ServiceResponse } from '@sdbn/common';
import { Subscription } from 'rxjs';
import { YardConfiguration } from '../../../../client/model/yard-configuration';

import { ConfirmDialogComponent, ConfirmDialogData } from '@sdbn/tmc-legacy';
import {
  TransportOption,
  TransportOptionColumn,
  TransportOptionSelection,
  TransportOptionsPanelModel,
} from '../../../../components/transport-options-panel/transport-options-panel.model';

@Component({
    selector: 'app-transport-creation-profiles',
    templateUrl: './transport-creation-profiles.component.html',
    styleUrls: ['./transport-creation-profiles.component.scss'],
    standalone: false
})
export class TransportCreationProfilesComponent
  extends AbstractWidgetComponent
  implements OnInit, OnDestroy {
  newId = -1;
  detailsForm: FormGroup;
  formSubscription: Subscription;

  numberPlateValidStatus: boolean = true;

  transportCreationProfileNumberPlatesChanged = false;

  profiles: TransportCreationProfile[] = [];
  selectedProfile: TransportCreationProfile;

  responseMessages: ResponseMessage[];
  isLoading = false;
  isChanging = false;

  tmcContextSubscription: Subscription;

  yardConfiguration: YardConfiguration;

  transportOptionsPanelModel: TransportOptionsPanelModel;

  get cancelEnabled() {
    return (
      !this.isLoading &&
      this.detailsForm != null &&
      (this.detailsForm.dirty || this.transportOptionsPanelModel?.dirty)
    );
  }

  get saveEnabled() {


    return (
      !this.isLoading &&
      this.detailsForm &&
      this.numberPlateValidStatus &&
      (this.detailsForm.dirty ||
        this.transportOptionsPanelModel?.dirty ||
        this.transportCreationProfileNumberPlatesChanged) &&
      this.transportOptionsPanelModel?.valid &&
      this.detailsForm.valid
    );
  }

  get displayNameVisible() {
    return (
      this.selectedProfile?.hasActionTablet
    );
  }

  get numberPlateEnabled() {
    return (
      this.selectedProfile?.hasNumberPlateList
    );
  }

  constructor(
    public translationService: TranslationService,
    private dialog: MatDialog,
    private appServerAPI: AppServerAPI,
    private tmcContextService: TmcContextService
  ) {
    super();

    this.detailsForm = new FormGroup({
      name: new FormControl('', [Validators.required]),
      displayName: new FormControl('', [Validators.required]),
      numberPlate: new FormControl('', []),
      carrier: new FormControl('', []),
      driver: new FormControl('', []),
      orderReference: new FormControl('', []),
      active: new FormControl('', []),
      allowParallelAccess: new FormControl('', []),
      withRouting: new FormControl('', []),
      hasActionTablet: new FormControl('', []),
      hasNumberPlateList: new FormControl('', []),
      hasQrCode: new FormControl('', [])
    });

  }

  ngOnInit() {
    this.formSubscription = this.detailsForm.valueChanges.subscribe(() => {
      if (!this.isChanging) {
        console.log('values changing');
        const values = this.detailsForm.getRawValue();
        this.selectedProfile.name = values.name;
        this.selectedProfile.displayName = values.displayName;
        this.selectedProfile.numberPlate = values.numberPlate;
        this.selectedProfile.carrier = values.carrier;
        this.selectedProfile.orderReference = values.orderReference;
        this.selectedProfile.active = values.active;

        this.selectedProfile.driver = values.driver;
        this.selectedProfile.allowParallelAccess = values.allowParallelAccess;
        this.selectedProfile.hasActionTablet = values.hasActionTablet;
        this.selectedProfile.hasNumberPlateList = values.hasNumberPlateList;
        this.selectedProfile.hasQrCode = values.hasQrCode;
        this.selectedProfile.withRouting = values.withRouting;

        this.updateRequiredFields();
      }
    });

    this.load();
  }

  ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
    if (this.tmcContextSubscription != null) {
      this.tmcContextSubscription.unsubscribe();
      this.tmcContextSubscription = null;
    }
  }

  numberPlateValidStatusChanged(valid: boolean) {
    this.numberPlateValidStatus = valid;
  }

  updateTransportCreationProfileNumberPlatesChanged(value: boolean) {
    this.transportCreationProfileNumberPlatesChanged = value;
    this.detailsForm.markAsDirty();
  }

  setSelected(profile: TransportCreationProfile) {
    // this.optionsDirty = false;
    if (
      this.selectedProfile != null &&
      this.selectedProfile.id === profile?.id
    ) {
      this.selectedProfile = profile;
      this.resetForm();
      return;
    }

    const oldSelected = this.selectedProfile;
    this.selectedProfile = null;

    if (
      oldSelected != null &&
      (this.detailsForm.dirty || this.transportOptionsPanelModel?.dirty)
    ) {
      const dialogData: ConfirmDialogData = {
        titleKey:
          'tmc-yard.ui.admin.transport-creation-profiles.discard.confirm-dialog.title',
        messageKey:
          'tmc-yard.ui.admin.transport-creation-profiles.discard.confirm-dialog.message',
        confirm: () => {
          this.doChangeSelection(profile);
        },
        cancel: () => {
          this.selectedProfile = oldSelected;
        },
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData,
      });
    } else {
      this.doChangeSelection(profile);
    }
  }

  private doChangeSelection(profile: TransportCreationProfile) {
    this.transportOptionsPanelModel = null;
    this.selectedProfile = profile;
    this.resetForm();
    if (this.selectedProfile != null) {
      this.createTransportOptionsModel();
    }
  }

  createTransportOptionsModel() {
    if (this.tmcContextSubscription != null) {
      this.tmcContextSubscription.unsubscribe();
    }

    this.tmcContextSubscription = this.tmcContextService.updated.subscribe(
      (next) => {
        if (next != null) {
          this.yardConfiguration = next;
          let columns = new Array<TransportOptionColumn>();
          for (let option of this.yardConfiguration.transportOptions) {
            while (
              option.columnIndex != null &&
              columns.length < option.columnIndex + 1
            ) {
              let column = new TransportOptionColumn();
              columns.push(column);
            }
            let column = columns[option.columnIndex];
            column.options.push(new TransportOption(option));
          }
          const selection = new Array<TransportOptionSelection>();
          for (const id of this.selectedProfile.optionIds) {
            selection.push(
              new TransportOptionSelection({
                transportOptionId: id,
                selected: true,
              })
            );
          }
          this.transportOptionsPanelModel = new TransportOptionsPanelModel({
            columns: columns,
            constraints: this.yardConfiguration.transportOptionConstraints,
            selection: selection,
          });
        }
      }
    );
  }

  private updateRequiredFields() {
    let oldChanging = this.isChanging;
    this.isChanging = true;
    if (this.displayNameVisible) {
      this.detailsForm.get('displayName').enable();
    } else {
      this.detailsForm.get('displayName').disable();
    }
    this.isChanging = oldChanging;
  }

  delete(rule: TransportCreationProfile) {
    this.responseMessages = [];

    const dialogData: ConfirmDialogData = {
      titleKey:
        'tmc-yard.ui.admin.transport-creation-profiles.delete.confirm-dialog.title',
      messageKey:
        'tmc-yard.ui.admin.transport-creation-profiles.delete.confirm-dialog.message',
      confirm: () => {
        this.isLoading = true;
        this.appServerAPI
          .execute<ServiceResponse>(
            '/client/admin/transport-creation-profile/delete',
            rule.id
          )
          .subscribe(
            (response) => {
              if (response.status === ResponseStatus.SUCCESS) {
                this.clearAndLoad();
              } else {
                this.responseMessages = response.responseMessages;
              }
              this.isLoading = false;
            },
            (error) => {
              console.log(error);
              this.isLoading = false;
            }
          );
      },
    };

    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData,
    });
  }

  load() {
    this.isLoading = true;
    this.responseMessages = [];
    this.appServerAPI
      .execute<TransportCreationProfile[]>(
        '/client/admin/transport-creation-profile/profiles',
        {}
      )
      .subscribe(
        (profiles) => {
          this.profiles = [];
          for (const profile of profiles) {
            this.profiles.push(new TransportCreationProfile(profile));
          }
          if (this.profiles.length > 0) {
            const selectionToRestore = this.profiles.find(
              (s) => s.id === this.selectedProfile?.id
            );
            if (selectionToRestore == null) {
              this.setSelected(this.profiles[0]);
            } else {
              this.setSelected(selectionToRestore);
            }
          }

          this.isLoading = false;
        },
        (error) => {
          console.error(error);
          this.isLoading = false;
        }
      );
  }

  cancel() {
    if (
      this.selectedProfile != null &&
      (this.detailsForm.dirty || this.transportOptionsPanelModel?.dirty)
    ) {
      const dialogData: ConfirmDialogData = {
        titleKey:
          'tmc-yard.ui.admin.transport-creation-profiles.discard.confirm-dialog.title',
        messageKey:
          'tmc-yard.ui.admin.transport-creation-profiles.discard.confirm-dialog.message',
        confirm: () => {
          this.resetForm();
          // this.optionsDirty = false;
          this.load();
          this.createTransportOptionsModel();
        },
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData,
      });
    } else {
      this.load();
    }
  }

  clear() {
    this.responseMessages = [];
    this.selectedProfile = null;
    this.selectedProfile = new TransportCreationProfile();
    this.resetForm();
    this.createTransportOptionsModel();
  }

  clearAndLoad() {
    this.clear();
    this.load();
  }

  public save() {
    this.isLoading = true;
    this.responseMessages = [];

    const optionIds = new Array<number>();
    for (const selection of this.transportOptionsPanelModel?.getOnlySelectedTransportOptionSelection()) {
      optionIds.push(selection.transportOptionId);
    }
    this.selectedProfile.optionIds = optionIds;
    console.log('profile', this.selectedProfile)
    this.appServerAPI
      .execute<ServiceResponse>(
        '/client/admin/transport-creation-profile/save',
        this.selectedProfile
      )
      .subscribe(
        (response) => {
          this.isLoading = false;
          if (response.status === ResponseStatus.SUCCESS) {
            this.detailsForm.markAsPristine();
            this.transportOptionsPanelModel.dirty = false;
            this.transportCreationProfileNumberPlatesChanged = false;
            this.load();
          } else {
            this.responseMessages = response.responseMessages;
          }
        },
        (error) => {
          console.log(error);
          this.isLoading = false;
        }
      );
  }

  public drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.profiles, event.previousIndex, event.currentIndex);
    // this.saveRulesIndex();
  }

  onContextChange(contextChange: ContextChange) { }

  onSetElementAndContext() { }

  onSelectionChanged(entry: MasterDetailListEntry) {
    this.setSelected(entry as TransportCreationProfile);
  }

  onAddEntry(parentPath: Array<MasterDetailListEntry>) {
    if (
      this.selectedProfile != null &&
      (this.detailsForm.dirty || this.transportOptionsPanelModel?.dirty)
    ) {
      const dialogData: ConfirmDialogData = {
        titleKey:
          'tmc-yard.ui.admin.transport-creation-profiles.discard.confirm-dialog.title',
        messageKey:
          'tmc-yard.ui.admin.transport-creation-profiles.discard.confirm-dialog.message',
        confirm: () => {
          this.clear();
        },
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData,
      });
    } else {
      this.clear();
    }
  }

  onDeleteEntry(entry: MasterDetailListEntry) {
    this.delete(entry as TransportCreationProfile);
  }

  private resetForm() {
    if (this.isChanging) {
      return;
    }

    this.isChanging = true;

    const value: any = {};

    if (this.selectedProfile != null) {
      value.name = this.selectedProfile.name;
      value.numberPlate = this.selectedProfile.numberPlate;
      value.carrier = this.selectedProfile.carrier;
      value.orderReference = this.selectedProfile.orderReference;
      value.active = this.selectedProfile.active;
      value.driver = this.selectedProfile.driver;
      value.allowParallelAccess = this.selectedProfile.allowParallelAccess;
      value.displayName = this.selectedProfile.displayName;
      value.hasActionTablet = this.selectedProfile.hasActionTablet;
      value.hasNumberPlateList = this.selectedProfile.hasNumberPlateList;
      value.hasQrCode = this.selectedProfile.hasQrCode;
      value.withRouting = this.selectedProfile.withRouting;
    }
    this.updateRequiredFields();
    this.detailsForm.patchValue(value);
    this.detailsForm.markAsPristine();

    console.log('details form is', this.detailsForm);

    this.isChanging = false;
  }

  createPDF() {
    // Trigger opening a new tab. Must be as close as possible to a user action event.
    const newWindow = window.open('', 'pdf');

    // keep focus in current window
    window.focus();

    const url = `/client/admin/transport-creation-profile/pdf`;
    this.appServerAPI
      .postWithArraybufferResponse<any>(url, {
        profileId: this.selectedProfile.id,
      })
      .subscribe(
        (result) => {
          const file = new Blob([result], { type: 'application/pdf' });
          const fileURL = URL.createObjectURL(file);

          // we can open the file in a new tab
          if (newWindow !== null) {
            newWindow.location.href = fileURL;
          } else {
            // Fallback, if initial opener does not work. But popup blockers will block this.
            window.open(fileURL);
          }
        },
        (error) => {
          console.error(error);
        }
      );
  }

  deleteNumberPlate(numberPlate: TransportCreationProfileNumberPlate) {
    this.selectedProfile.transportCreationProfileNumberPlates.filter((oldNumberPlate) => oldNumberPlate.numberPlate != numberPlate.numberPlate);
    this.updateTransportCreationProfileNumberPlatesChanged(true);
  }

  addNewNumberPlate(numberPlate: TransportCreationProfileNumberPlate) {
    this.selectedProfile.transportCreationProfileNumberPlates.push(numberPlate);
    this.updateTransportCreationProfileNumberPlatesChanged(true);
  }

  changeNumberPlateActive(changedNumberPlate: TransportCreationProfileNumberPlate) {
    const index = this.selectedProfile.transportCreationProfileNumberPlates.findIndex(numberPlate => numberPlate.numberPlate == changedNumberPlate.numberPlate);
    this.selectedProfile.transportCreationProfileNumberPlates[index] = changedNumberPlate;
    this.updateTransportCreationProfileNumberPlatesChanged(true);
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }
}
